import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert, Table } from 'react-bootstrap';
import { FiArrowLeft, FiCloudOff, FiUser, FiBriefcase, FiMapPin, FiCalendar, FiTag, FiGlobe, FiCheckCircle, FiCopy } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchForecastNotTriggeredByIdRequest } from '../../store/actions/tripsActions';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicyInfoCard from '../../components/Common/PolicyInfoCard';
import './AdminTripPolicyDetail.css';
import { resolvePrecipTrigger } from '../../utils/precipTrigger';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children || 'N/A'}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const getDeep = (obj, path, fallback = 'N/A') => {
    try {
        const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        return val ?? fallback;
    } catch {
        return fallback;
    }
};

const AdminForecastNotTriggeredDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { forecastNotTriggeredId } = useParams();
    const { selectedForecastNotTriggered } = useSelector((state) => state.trips);
    const [copiedObservation, setCopiedObservation] = useState(false);
    const [copiedForecast, setCopiedForecast] = useState(false);

    useEffect(() => {
        if (forecastNotTriggeredId) {
            dispatch(fetchForecastNotTriggeredByIdRequest(forecastNotTriggeredId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forecastNotTriggeredId]);

    const data = selectedForecastNotTriggered?.data || null;
    const policyInfo = data?.policy_info || {};
    const clientInfo = data?.client_info || {};
    const activityInfo = data?.activity_info || {};
    const partnerInfo = data?.partner_info || {};
    const referenceInfo = data?.reference_info || {};
    const subCoverageRaw = referenceInfo?.SubCoverage || referenceInfo?.subcoverage_trips || {};
    const subCoverage = Array.isArray(subCoverageRaw) ? (subCoverageRaw[0] || {}) : (subCoverageRaw || {});
    const firstSubNotTriggered = Array.isArray(referenceInfo?.subcoverage_trips) && referenceInfo.subcoverage_trips[0]
        ? referenceInfo.subcoverage_trips[0]
        : subCoverage;
    const formatWeatherParameter = (value) => {
        if (value == null || value === '') return 'N/A';
        if (Array.isArray(value)) return value.filter(Boolean).join(', ') || 'N/A';
        return String(value);
    };
    const checkClaims = data?.CheckClaims || {};
    const lastObservationInfo = data?.last_observation_info || {};
    const forecastInfo = data?.forecast_info || {};
    const precip = resolvePrecipTrigger(
        [checkClaims.prcp_is_cumulated, data?.prcp_is_cumulated, referenceInfo.prcp_is_cumulated],
        [checkClaims, data, firstSubNotTriggered]
    );
    const displayOrNa = (value) => (value == null || value === '' ? 'N/A' : value);

    const copyObservationDataToClipboard = async () => {
        if (!lastObservationInfo?.last_observation_data || !Array.isArray(lastObservationInfo.last_observation_data) || lastObservationInfo.last_observation_data.length === 0) return;

        const headers = ['Heure', 'Précipitation (mm)'];
        const rows = lastObservationInfo.last_observation_data.map(data => [
            data.time || '',
            data.prcp || '0,0'
        ]);

        const csvContent = [
            headers.join('\t'),
            ...rows.map(row => row.join('\t'))
        ].join('\n');

        try {
            await navigator.clipboard.writeText(csvContent);
            setCopiedObservation(true);
            setTimeout(() => setCopiedObservation(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = csvContent;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedObservation(true);
                setTimeout(() => setCopiedObservation(false), 2000);
            } catch (fallbackErr) {
                console.error('Erreur lors de la copie:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const copyForecastDataToClipboard = async () => {
        if (!forecastInfo?.weather_data || !Array.isArray(forecastInfo.weather_data) || forecastInfo.weather_data.length === 0) return;

        const headers = ['Date', 'Heure', 'Précipitation (mm)'];
        const rows = forecastInfo.weather_data.map(data => {
            const date = data.forecast_day && data.forecast_month && data.forecast_year
                ? `${data.forecast_day}/${data.forecast_month}/${data.forecast_year}`
                : '';
            return [
                date,
                data.forecast_hour || '',
                data.value || '0,0'
            ];
        });

        const csvContent = [
            headers.join('\t'),
            ...rows.map(row => row.join('\t'))
        ].join('\n');

        try {
            await navigator.clipboard.writeText(csvContent);
            setCopiedForecast(true);
            setTimeout(() => setCopiedForecast(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = csvContent;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedForecast(true);
                setTimeout(() => setCopiedForecast(false), 2000);
            } catch (fallbackErr) {
                console.error('Erreur lors de la copie:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    if (selectedForecastNotTriggered.loading) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision non déclenchée</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de la prévision non déclenchée...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedForecastNotTriggered.error) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision non déclenchée</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedForecastNotTriggered.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision non déclenchée</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée disponible pour l'identifiant: {forecastNotTriggeredId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-trip-policy-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiCloudOff className="me-2" />
                                Détail de la prévision non déclenchée
                            </h2>
                            <p className="text-muted mb-0">Code: {data.code || 'N/A'}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/forecasts-not-triggered')}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {/* Informations principales */}
                    <Section title="Informations principales">
                        <Row>
                            <Col md={6}>
                                <Label>Code</Label>
                                <Value>{data.code}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={data.status === 'Created' ? 'info' : 'secondary'}>
                                        {data.status}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date de création</Label>
                                <Value>{data.created_date || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Basé sur</Label>
                                <Value>{data.trigger_based_on || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Créé par</Label>
                                <Value>{data.created_by || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Coordonnées</Label>
                                <Value>{data.latitude}, {data.longitude}</Value>
                            </Col>
                        </Row>
                    </Section>


                    {checkClaims && Object.keys(checkClaims).length > 0 && (
                        <Section title="Vérification du sinistre">
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label><FiCheckCircle className="me-1" />Forecast déclenché</Label>
                                    <Value>
                                        <Badge bg={checkClaims.is_triggered_forecast === true ? 'success' : 'secondary'}>
                                            {checkClaims.is_triggered_forecast === true ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Observation déclenchée</Label>
                                    <Value>
                                        {(() => {
                                            const isTriggeredObs = checkClaims.is_triggered_observation;
                                            if (isTriggeredObs === null || isTriggeredObs === undefined) {
                                                return <Badge bg="warning">En attente de traitement</Badge>;
                                            }
                                            return (
                                                <Badge bg={isTriggeredObs === true ? 'success' : 'secondary'}>
                                                    {isTriggeredObs === true ? 'Oui' : 'Non'}
                                                </Badge>
                                            );
                                        })()}
                                    </Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Paramètre météo</Label>
                                    <Value>
                                        {formatWeatherParameter(
                                            firstSubNotTriggered?.weather_parameter
                                            ?? referenceInfo.weather_parameter
                                        )}
                                    </Value>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label>Dernière modification</Label>
                                    <Value>{checkClaims.check_claim_last_modified_by || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Date modification</Label>
                                    <Value>{checkClaims.check_claim_last_modified_at || 'N/A'}</Value>
                                </Col>
                            </Row>
                            {(() => {
                                const weatherParams = Array.isArray(firstSubNotTriggered?.weather_parameter)
                                    ? firstSubNotTriggered.weather_parameter
                                    : (firstSubNotTriggered?.weather_parameter ? [firstSubNotTriggered.weather_parameter] : []);
                                const paramsNorm = weatherParams.map((p) => String(p).toLowerCase());
                                const hasPrecip = paramsNorm.length === 0
                                    || paramsNorm.some((p) => p.includes('precip'));
                                const hasTemp = paramsNorm.length === 0
                                    || paramsNorm.some((p) => p.includes('temp'));

                                const tempTriggered = checkClaims.temperature_is_triggered_forecast;
                                const precipTriggered = checkClaims.precipitation_is_triggered_forecast;
                                const tempHours = checkClaims.temperature_count_hours_triggered;
                                const precipHours = checkClaims.precipitation_count_hours_triggered;

                                const boolBadge = (value) => {
                                    if (value === null || value === undefined) {
                                        return <Badge bg="warning">En attente de traitement</Badge>;
                                    }
                                    return (
                                        <Badge bg={value === true ? 'success' : 'secondary'}>
                                            {value === true ? 'Oui' : 'Non'}
                                        </Badge>
                                    );
                                };

                                return (
                                    <>
                                        <Row className="mb-2">
                                            <Col md={4}>
                                                <Label>Température déclenchée (forecast)</Label>
                                                <Value>
                                                    {!hasTemp
                                                        ? <Badge bg="secondary">Non applicable</Badge>
                                                        : boolBadge(tempTriggered)}
                                                </Value>
                                            </Col>
                                            <Col md={4}>
                                                <Label>Nb heures déclenchées température</Label>
                                                <Value>
                                                    {!hasTemp
                                                        ? '—'
                                                        : (tempHours === null || tempHours === undefined ? '—' : tempHours)}
                                                </Value>
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col md={4}>
                                                <Label>Précipitation déclenchée (forecast)</Label>
                                                <Value>
                                                    {!hasPrecip
                                                        ? <Badge bg="secondary">Non applicable</Badge>
                                                        : boolBadge(precipTriggered)}
                                                </Value>
                                            </Col>
                                            <Col md={4}>
                                                <Label>Nb heures déclenchées précipitation</Label>
                                                <Value>
                                                    {!hasPrecip
                                                        ? '—'
                                                        : (precipHours === null || precipHours === undefined ? '—' : precipHours)}
                                                </Value>
                                            </Col>
                                        </Row>
                                    </>
                                );
                            })()}
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label>Vent déclenché (forecast)</Label>
                                    <Value>
                                        {(() => {
                                            const windTriggered = checkClaims.wind_is_triggered_forecast;
                                            if (windTriggered === null || windTriggered === undefined) {
                                                return <Badge bg="warning">En attente de traitement</Badge>;
                                            }
                                            return (
                                                <Badge bg={windTriggered === true ? 'success' : 'secondary'}>
                                                    {windTriggered === true ? 'Oui' : 'Non'}
                                                </Badge>
                                            );
                                        })()}
                                    </Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    <PolicyInfoCard
                        companyName={partnerInfo.company_name}
                        policyCode={policyInfo.policy_code}
                        reservationNumber={activityInfo.reservation_number}
                        stayStartDate={activityInfo.activity_start_date}
                        stayEndDate={activityInfo.activity_end_date}
                        newStayStartDate={activityInfo.new_trip_start_date}
                        newStayEndDate={activityInfo.new_trip_end_date}
                        effectiveStartDate={activityInfo.trip_effective_start_date}
                        effectiveEndDate={activityInfo.trip_effective_end_date}
                        newEffectiveStartDate={activityInfo.new_trip_effective_start_date}
                        newEffectiveEndDate={activityInfo.new_trip_effective_end_date}
                        referenceName={referenceInfo.reference_name}
                        address={
                            activityInfo.combined_address
                            || [
                                activityInfo.activity_address,
                                activityInfo.activity_zip_code,
                                activityInfo.activity_city,
                                activityInfo.activity_country,
                            ].filter(Boolean).join(', ')
                            || forecastInfo.address
                        }
                        latitude={activityInfo.latitude ?? data?.latitude ?? forecastInfo.latitude}
                        longitude={activityInfo.longitude ?? data?.longitude ?? forecastInfo.longitude}
                        dailySumInsuredMaintain={policyInfo.daily_sum_insured_maintain}
                        dailySumInsuredCancellation={policyInfo.daily_sum_insured_cancellation}
                        totalSumInsuredMaintain={policyInfo.total_sum_insured_maintain}
                        totalSumInsuredCancellation={policyInfo.total_sum_insured_cancellation}
                    />

                    {/* Informations prévision — aligné trips/check-claims */}
                    {forecastInfo && forecastInfo.forecast_id && (
                        <Section title="Informations prévision">
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label>Source de données</Label>
                                    <Value>{forecastInfo.data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{forecastInfo.total_mm_prc || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label>Coordonnées</Label>
                                    <Value>
                                        {forecastInfo.latitude || 'N/A'}, {forecastInfo.longitude || 'N/A'}
                                    </Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Jour du trip</Label>
                                    <Value>{forecastInfo.trip_day || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Date init</Label>
                                    <Value>
                                        {forecastInfo.meteomatics_init_day || '—'}/
                                        {forecastInfo.meteomatics_init_month || '—'}/
                                        {forecastInfo.meteomatics_init_year || '—'}
                                    </Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Heure exécution (UTC)</Label>
                                    <Value>{forecastInfo.meteomatics_run_hour_utc || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}>
                                    <Label>Adresse</Label>
                                    <Value>{forecastInfo.address || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <Label>Dernière modification</Label>
                                    <Value>{forecastInfo.forecast_last_modified_at || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Modifié par</Label>
                                    <Value>{forecastInfo.forecast_last_modified_by || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <WeatherDataSections
                                weatherData={forecastInfo.weather_data}
                                weatherTemp={forecastInfo.weather_temp}
                                weatherWind={forecastInfo.weather_wind}
                                enableCopy
                                onCopyPrecipitation={copyForecastDataToClipboard}
                                copied={copiedForecast}
                                tableSize="sm"
                                bordered
                                className="mt-3"
                                idPrefix="forecast-nt"
                            />
                        </Section>
                    )}

                    {/* Informations référence */}
                    <Section title="Informations référence">
                        <Row className="mb-2">
                            <Col md={4}>
                                <Label>Nom référence</Label>
                                <Value>{referenceInfo.reference_name || 'N/A'}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Paramètre météo</Label>
                                <Value>{formatWeatherParameter(firstSubNotTriggered?.weather_parameter ?? referenceInfo.weather_parameter)}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Déclenchement cumulé</Label>
                                <Value>
                                    <Badge bg={precip.isCumulated ? 'success' : 'secondary'}>
                                        {precip.isCumulated ? 'Oui' : 'Non'}
                                    </Badge>
                                    <span className="ms-2 text-muted small">({precip.modeLabel})</span>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            {precip.isCumulated ? (
                                <>
                                    <Col md={4}>
                                        <Label>Seuil bas cumulé des précipitations (mm)</Label>
                                        <Value>{displayOrNa(precip.lowThreshold)}</Value>
                                    </Col>
                                    <Col md={4}>
                                        <Label>Seuil haut cumulé des précipitations (mm)</Label>
                                        <Value>{displayOrNa(precip.upperThreshold)}</Value>
                                    </Col>
                                </>
                            ) : (
                                <>
                                    <Col md={4}>
                                        <Label>Seuil horaire inférieur des précipitations (mm/h)</Label>
                                        <Value>{displayOrNa(precip.lowThreshold)}</Value>
                                    </Col>
                                    <Col md={4}>
                                        <Label>Seuil horaire supérieur des précipitations (mm/h)</Label>
                                        <Value>{displayOrNa(precip.upperThreshold)}</Value>
                                    </Col>
                                    <Col md={4}>
                                        <Label>Nombre d’heures de déclenchement des précipitations</Label>
                                        <Value>{displayOrNa(precip.hours)}</Value>
                                    </Col>
                                </>
                            )}
                        </Row>
                        <Row className="mb-2">
                            <Col md={4}>
                                <Label>Heure déclencheur (température)</Label>
                                <Value>{firstSubNotTriggered?.temperature_trigger_hour_inf ?? firstSubNotTriggered?.temperature_trigger_hour_inf ?? 'N/A'}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Nombre d'heures (température)</Label>
                                <Value>{firstSubNotTriggered?.temperature_trigger_number_hours ?? checkClaims.temperature_trigger_number_hours ?? 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Dernière observation */}
                    {lastObservationInfo && lastObservationInfo.last_observation_id && (
                        <Section title="Dernière observation">
                            <Row className="mb-2">
                                <Col md={4}><Label>Source de données</Label><Value>{lastObservationInfo.last_data_source || 'N/A'}</Value></Col>
                                <Col md={4}><Label>Station</Label><Value>{lastObservationInfo.last_station_name || 'N/A'}</Value></Col>
                                <Col md={4}><Label>ID Station</Label><Value>{lastObservationInfo.last_station_id || 'N/A'}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Jour d'observation</Label><Value>{lastObservationInfo.trip_day || 'N/A'}</Value></Col>
                                <Col md={4}><Label>Date d'initialisation</Label><Value>
                                    {lastObservationInfo.last_observation_init_day || '—'}/
                                    {lastObservationInfo.last_observation_init_month || '—'}/
                                    {lastObservationInfo.last_observation_init_year || '—'}
                                </Value></Col>
                                <Col md={4}><Label>Heure d'exécution</Label><Value>{lastObservationInfo.last_observation_run_hour || 'N/A'}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Coordonnées station</Label><Value>
                                    {lastObservationInfo.last_latitude_station || 'N/A'}, {lastObservationInfo.last_longitude_station || 'N/A'}
                                </Value></Col>
                                <Col md={4}><Label>Total mm précipitation</Label><Value>{lastObservationInfo.last_total_mm_prc || '0,0'}</Value></Col>
                                <Col md={4}><Label>Dernière modification</Label><Value>{lastObservationInfo.last_observation_last_modified_at || 'N/A'}</Value></Col>
                            </Row>
                            {lastObservationInfo.last_observation_data && Array.isArray(lastObservationInfo.last_observation_data) && lastObservationInfo.last_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Label>Données d'observation</Label>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={copyObservationDataToClipboard}
                                            className="d-flex align-items-center"
                                        >
                                            <FiCopy className="me-2" />
                                            {copiedObservation ? 'Copié!' : 'Copier le tableau'}
                                        </Button>
                                    </div>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lastObservationInfo.last_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Section>
                    )}

                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminForecastNotTriggeredDetail;

